import {
  Alert,
  Box,
  Button,
  HStack,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { useMemo } from "react";
import { isScoreFiltersEmpty } from "../../../../map/models/MapFilters";
import { MultiContextPieChart } from "../pie-chart/MultiContextPieChart";
import { ColorBySelector } from "../ColorByLegend/ColorBySelector";
import { AttributeFilters } from "../filters/AttributeFilters";
import { ScoreFilters } from "../filters/ScoreFilters";
import { TreatmentFilters } from "../filters/TreatmentFilters";
import { ForecastControls } from "../forecast/ForecastControls";
import { LifeCycleStageFilters } from "../filters/LifeCycleStageFilters";
import { useStores } from "../../../../shared/stores/RootStore";
import { formatDistanceByUnit } from "../../../../shared/utils/format-utils";
import { LifeCycleStage } from "../../../../osaway/models/LifeCycleStage";
import { OsaWayFilters } from "../filters/OsaWayFilters";
import useAutoResizeMap from "../../../../hooks/useAutoResizeMap";
import { AssessmentTypeFilters } from "../filters/AssessmentTypeFilters";
import { DistanceUnit } from "@keita/lanepatrol-sdk";

const FiltersBase: React.FC = () => {
  useAutoResizeMap();
  const {
    mapStore,
    filterStore,
    authStore,
    osaWayStore,
    hlaStore,
    projectStore,
  } = useStores();
  const selectedOsaWay = osaWayStore.getSelected();

  const nodes = filterStore.nodesAvailableInCurrentMode;
  const distanceUnit = projectStore.distanceUnit;

  const totalDistance = useMemo(() => {
    const osaNodeDistance = [...nodes.values()].reduce(
      (totalLength, nodes) =>
        totalLength +
        nodes.reduce(
          (routeLength, node) => routeLength + node.distanceToNext,
          0,
        ),
      0,
    );

    return (
      osaNodeDistance +
      hlaStore.ways.reduce((acc, hlaWay) => acc + hlaWay.lengthM, 0)
    );
  }, [nodes, hlaStore.ways]);

  const selectedStage = selectedOsaWay?.lifeCycleStage;
  const codingNotDone =
    selectedStage === LifeCycleStage.Design ||
    selectedStage === LifeCycleStage.DataCollection ||
    selectedStage === LifeCycleStage.Coding;

  return (
    <VStack
      overflowY="scroll"
      gap={6}
      py={4}
      px={4}
      alignItems="stretch"
      w={320}
      flexShrink={0}
    >
      <Stack>
        <HStack>
          <Text>{t(`map:header.filterBy`)}</Text>
          <Spacer />
          <Button
            size="sm"
            fontSize={"sm"}
            colorPalette="primary"
            variant="ghost"
            onClick={() => {
              filterStore.resetFilters();
            }}
            title={t(`map:filters.clearAllTitle`)}
            disabled={
              isScoreFiltersEmpty(filterStore.scoreFilters) &&
              !filterStore.attributeFilters.length &&
              !filterStore.osaWayFilters.length &&
              !filterStore.lifeCycleStageFilters.length &&
              !filterStore.treatmentFilters.length
            }
          >
            {t(`map:filters.clearAllTitle`)}
          </Button>
        </HStack>

        <AssessmentTypeFilters />
        <OsaWayFilters />
        {authStore.isAuthenticated && <LifeCycleStageFilters />}
        <TreatmentFilters />
        <ScoreFilters />
        <AttributeFilters />
      </Stack>
      <Box>
        <Text fontSize="xs" fontWeight="semibold">
          {t("map:filters.distanceLabel")}
        </Text>

        <Text fontSize="sm">
          {t("map:filters.distanceSummary", {
            filtered: formatDistanceByUnit(
              filterStore.filteredDistance,
              distanceUnit,
            ),
            total: formatDistanceByUnit(totalDistance, distanceUnit),
            unit: distanceUnit === DistanceUnit.Imperial ? "mi" : "km",
          })}
        </Text>
      </Box>

      {!codingNotDone ? (
        <ForecastControls />
      ) : (
        <Alert.Root>
          <Alert.Indicator />
          <Alert.Description>
            {t(`map:filters.forecastUnavailable`)}
          </Alert.Description>
        </Alert.Root>
      )}

      <Stack>
        <Text>{t(`map:header.lineColor`)}</Text>
        <ColorBySelector
          value={mapStore.colorBy}
          onChange={(colorBy) => mapStore.setColorBy({ ...colorBy })}
        />
        <Spacer />
        <MultiContextPieChart colorBy={mapStore.colorBy} />
      </Stack>
    </VStack>
  );
};

export const FiltersBar = observer(FiltersBase);
